import React, { useReducer, useRef, useEffect, useCallback, useMemo, useState, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { playerReducer, createInitialState } from './playerReducer';
import { useMediaEvents } from './hooks/useMediaEvents';
import { useKeyboardNav, KEYBOARD_SHORTCUTS } from './hooks/useKeyboardNav';
import { usePlaybackShuttle } from './hooks/usePlaybackShuttle';
import { useFullscreen } from './hooks/useFullscreen';
import { useFrameAccurateTime } from './hooks/useFrameAccurateTime';
import { useCaptions } from './hooks/useCaptions';
import { useResponsiveControls } from './hooks/useResponsiveControls';
import { useHeatmapTracker } from './hooks/useHeatmapTracker';
import { useCaptionSpeech } from './hooks/useCaptionSpeech';
import { isSpeechSynthesisSupported } from './utils/captionSpeechSettings';
import { ControlBar } from './components/ControlBar';
import { PlayButton } from './components/PlayButton';
import { SkipBackButton } from './components/SkipBackButton';
import { SkipForwardButton } from './components/SkipForwardButton';
import { VolumeControl } from './components/VolumeControl';
import { SeekBar } from './components/SeekBar';
import { TimecodeDisplay } from './components/TimecodeDisplay';
import { TimecodePopover } from './components/TimecodePopover';
import { FullscreenButton } from './components/FullscreenButton';
import { HeatmapOverlay } from './components/HeatmapOverlay';
import { ActionsMenu } from './components/ActionsMenu';
import { FollowerReadyButton } from './components/FollowerReadyButton';
import { CaptionsButton } from './components/CaptionsButton';
import { CaptionOverlay } from './components/CaptionOverlay';
import { WaveformDisplay } from './components/WaveformDisplay';
import { DownloadOverlay } from './components/DownloadOverlay';
import { EncodingProgressOverlay } from './components/EncodingProgressOverlay';
import { UnplayableSourceOverlay } from './components/UnplayableSourceOverlay';
import { StatusOverlay } from './components/StatusOverlay';
import { SidePanel } from './components/SidePanel';
import { showPlaybackSettingsDialog } from './utils/playbackSettingsDialog';
import { usePlaybackErrorHandler } from './hooks/usePlaybackErrorHandler';
import { useStatusOverlay } from './hooks/useStatusOverlay';
import { useVideoOverlays } from './hooks/useVideoOverlays';
import { VideoOverlayLayer } from './components/VideoOverlayLayer';
import type { OverflowMenuItem } from './components/ActionsMenu';
import type {KollabPlayerProps, KollabPlayerAPI, CommentMarker, MediaSource, PlayerEventCallback} from './types';
// `PlayerEventType` is a `const enum`; it needs to be a value import so
// references like `PlayerEventType.SPEED_CHANGE` resolve at runtime.
import { PlayerEventType } from './types';
import { captureFrame } from '../annotations/utils/frameExport';
import type { FrameExportOptions } from '../annotations/utils/frameExport';
import { computeContentBoxPosition } from './utils/computeContentBoxPosition';
import { timecodeToFrameCount, formatTimecode } from './utils/timecodeFormatter';

const KollabPlayer = React.forwardRef<KollabPlayerAPI, KollabPlayerProps>(
  function KollabPlayer(props, ref) {
  const {
    mediaElementId,
    mediaElementRef: mediaElementRefProp,
    fullscreenTargetRef: fullscreenTargetRefProp,
    mediaType,
    fileId,
    waveformUrl,
    waveformColor,
    fps,
    startFrame: startFrameProp = 0,
    startTimecode,
    dropFrame = false,
    enableHeatmap = true,
    trackHeatmap = true,
    linkId,
    enableCaptions = true,
    autoplay = false,
    startTime,
    thumbnailSpriteUrl,
    comments: initialComments,
    onTimeUpdate,
    onPlay,
    onPause,
    onEnded,
    onSeek,
    onBeforeSeek,
    onFullscreenChange,
    canDownload = true,
    downloadUrl,
    fileTitle,
    fileTypeName,
    fileSize,
    fileIconUrl,
    sidePanelElementId,
    sidePanelWidth: sidePanelWidthProp,
    sidePanelTooltip,
    onMounted,
    isActive: isActiveProp = true,
    syncedSession,
    syncedSessionManager,
    onMediaReady,
  } = props;

  const [state, dispatch] = useReducer(playerReducer, undefined, createInitialState);

  const isFollower = syncedSession?.enabled === true && syncedSession.role === 'follower';
  const isLeader = syncedSession?.enabled === true && syncedSession.role === 'leader';

  // Convert startTimecode string to a frame offset. Takes precedence over startFrame.
  const startFrame = useMemo(() => {
    if (startTimecode && fps) {
      return timecodeToFrameCount(startTimecode, fps, dropFrame);
    }
    return startFrameProp;
  }, [startTimecode, fps, dropFrame, startFrameProp]);

  const isFullscreenRef = useRef(false);
  isFullscreenRef.current = state.isFullscreen;
  const [comments, setComments] = useState<CommentMarker[]>(initialComments ?? []);
  const [detectedIsVideo, setDetectedIsVideo] = useState(true);
  const [annotationEditorActive, setAnnotationEditorActive] = useState(false);

  // Duration display mode: 'duration' shows total length, 'remaining' shows time left
  const LS_DURATION_MODE_KEY = 'player.durationDisplayMode';
  const [durationMode, setDurationMode] = useState<'duration' | 'remaining'>(() => {
    try {
      const saved = localStorage.getItem(LS_DURATION_MODE_KEY);
      if (saved === 'remaining') return 'remaining';
    } catch { /* ignore */ }
    return 'duration';
  });

  const toggleDurationMode = useCallback(() => {
    setDurationMode((prev) => {
      const next = prev === 'duration' ? 'remaining' : 'duration';
      try { localStorage.setItem(LS_DURATION_MODE_KEY, next); } catch { /* ignore */ }
      return next;
    });
  }, []);

  const containerRef = useRef<HTMLDivElement>(null);
  const mediaRef = useRef<HTMLMediaElement | null>(null);
  // Used for aria-controls on child control buttons. Falls back to the
  // media element's own DOM id when only a ref was provided.
  const effectiveMediaElementId =
    mediaElementId ?? mediaElementRefProp?.current?.id ?? '';
  const seekBarElRef = useRef<HTMLDivElement | null>(null);
  const [heatmapRect, setHeatmapRect] = useState<{ left: number; width: number } | null>(null);
  const [seekBarHovered, setSeekBarHovered] = useState(false);
  const fullscreenTargetRef = useRef<HTMLElement | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sidePanelContentRef = useRef<HTMLDivElement>(null);
  // Track adopted element and its original position for restoration
  const sidePanelAdoptedElRef = useRef<Element | null>(null);
  const sidePanelElementOriginRef = useRef<{ parent: HTMLElement; nextSibling: Node | null } | null>(null);
  const isOverControlsRef = useRef(false);
  const userPlaybackRateRef = useRef(1.0);
  const syncAnnotationRootsRef = useRef<(() => void) | null>(null);
  const computeFitRef = useRef<(() => void) | null>(null);
  const onMediaReadyRef = useRef(onMediaReady);
  onMediaReadyRef.current = onMediaReady;

  // Resolve media element reference and detect type
  useEffect(() => {
    // Runtime validation: exactly one of mediaElementId or mediaElementRef must be provided
    const hasId = typeof mediaElementId === 'string' && mediaElementId.length > 0;
    const hasRef = !!mediaElementRefProp;
    if (hasId && hasRef) {
      console.warn('KollabPlayer: both mediaElementId and mediaElementRef were provided. Using mediaElementRef and ignoring mediaElementId.');
    } else if (!hasId && !hasRef) {
      console.warn('KollabPlayer: neither mediaElementId nor mediaElementRef was provided. The player will not adopt a media element.');
    }

    let el: HTMLMediaElement | null = null;
    if (hasRef) {
      el = mediaElementRefProp!.current;
    } else if (hasId) {
      el = document.getElementById(mediaElementId!) as HTMLMediaElement | null;
    }
    mediaRef.current = el;
    if (el) {
      el.removeAttribute('controls');
      el.setAttribute('disablePictureInPicture', 'disablePictureInPicture');
      setDetectedIsVideo(el instanceof HTMLVideoElement);
      // Prefer an explicit fullscreen target ref if provided; otherwise fall
      // back to the media element's parent (e.g. .player-stage) which contains
      // both the <video> and the React controls mount point.
      fullscreenTargetRef.current =
        fullscreenTargetRefProp?.current ?? el.parentElement;
      fullscreenTargetRef.current?.classList.add('kollab-player-wrapper');
    }
    return () => {
      fullscreenTargetRef.current?.classList.remove('kollab-player-wrapper');
    };
  }, [mediaElementId, mediaElementRefProp, fullscreenTargetRefProp]);

  // Video fit — contract wrapper width to match video content edges when
  // the wrapper is height-constrained (e.g. max-height set on .player-stage).
  // Uses the video's aspect ratio to compute the correct width so there
  // are no black side-bars; whitespace appears around the player instead.
  const VIDEO_MIN_WIDTH = 300;
  useEffect(() => {
    const el = mediaRef.current;
    if (!el || !(el instanceof HTMLVideoElement)) return;
    const wrapper = fullscreenTargetRef.current;
    if (!wrapper) return;

    // Seed aspect ratio from the HTML width/height attributes (set from
    // server-side metadata in PlayerSlot) so computeFit() can size the
    // wrapper correctly before the browser fires loadedmetadata.
    const attrW = Number(el.getAttribute('width')) || 0;
    const attrH = Number(el.getAttribute('height')) || 0;
    let aspectRatio = attrW > 0 && attrH > 0 ? attrW / attrH : 0;
    if (aspectRatio) {
      wrapper.style.setProperty('--video-ar', `${attrW} / ${attrH}`);
      wrapper.style.setProperty('--video-ar-num', String(aspectRatio));
    }

    let lastMaxWidth = '';

    // Sync annotation roots to match the video's object-fit:contain area.
    // Computes the exact pixel position and size of the video content
    // within the wrapper, then positions the annotation root to match.
    const syncAnnotationRoots = () => {
      if (!aspectRatio) return;
      // Only sync viewer roots — editor roots are managed by index.ts syncEditorRoot
      const roots = wrapper.querySelectorAll('[data-annotation-root="viewer"]');
      if (roots.length === 0) return;

      const wrapperRect = wrapper.getBoundingClientRect();
      const videoRect = el.getBoundingClientRect();

      const pos = computeContentBoxPosition({
        videoNativeWidth: el.videoWidth,
        videoNativeHeight: el.videoHeight,
        videoElementWidth: el.clientWidth,
        videoElementHeight: el.clientHeight,
        videoTopInWrapper: videoRect.top - wrapperRect.top,
        videoLeftInWrapper: videoRect.left - wrapperRect.left,
      });
      if (!pos) return;

      roots.forEach((root) => {
        const htmlRoot = root as HTMLElement;
        htmlRoot.style.position = 'absolute';
        htmlRoot.style.width = pos.width + 'px';
        htmlRoot.style.height = pos.height + 'px';
        htmlRoot.style.top = pos.top + 'px';
        htmlRoot.style.left = pos.left + 'px';
        htmlRoot.style.right = 'auto';
        htmlRoot.style.bottom = 'auto';
        htmlRoot.style.margin = '0';
      });
    };

    syncAnnotationRootsRef.current = syncAnnotationRoots;

    const computeFit = () => {
      syncAnnotationRoots();

      if (!aspectRatio || isFullscreenRef.current) {
        if (lastMaxWidth !== '') {
          wrapper.style.maxWidth = '';
          wrapper.style.margin = '';
          lastMaxWidth = '';
        }
        return;
      }

      // When the annotation editor is active, the wrapper dimensions are
      // frozen so the player stays the same size while the video shrinks.
      if (wrapper.classList.contains('kollab-annotation-active')) return;

      const videoHeight = el.clientHeight;
      if (videoHeight <= 0) return;

      // Content-box width of the video area, derived from its height + aspect.
      const contentWidth = Math.round(videoHeight * aspectRatio);

      // When the wrapper uses box-sizing: border-box, the max-width we write
      // below applies to the BORDER BOX (content + padding + border). If we
      // pass `contentWidth` straight through, the border width is silently
      // subtracted from the inner area on the next layout pass, the video
      // shrinks, computeFit runs again, and the wrapper shrinks by the border
      // width every iteration — a classic frame-over-frame shrink bug.
      // Add the wrapper's horizontal border + padding back when box-sizing is
      // border-box so the writes round-trip correctly.
      const wrapperStyle = window.getComputedStyle(wrapper);
      const isBorderBox = wrapperStyle.boxSizing === 'border-box';
      const horizontalChrome = isBorderBox
        ? (parseFloat(wrapperStyle.borderLeftWidth) || 0)
          + (parseFloat(wrapperStyle.borderRightWidth) || 0)
          + (parseFloat(wrapperStyle.paddingLeft) || 0)
          + (parseFloat(wrapperStyle.paddingRight) || 0)
        : 0;

      const targetBoxWidth = contentWidth + horizontalChrome;
      const parentWidth = wrapper.parentElement?.clientWidth ?? targetBoxWidth;

      let newMaxWidth: string;
      if (targetBoxWidth < parentWidth - 1) {
        newMaxWidth = Math.max(targetBoxWidth, VIDEO_MIN_WIDTH + horizontalChrome) + 'px';
      } else {
        newMaxWidth = '';
      }

      // Skip DOM writes when the value hasn't changed to avoid
      // ResizeObserver feedback loops that thrash layout.
      if (newMaxWidth !== lastMaxWidth) {
        lastMaxWidth = newMaxWidth;
        wrapper.style.maxWidth = newMaxWidth;
        wrapper.style.margin = newMaxWidth ? '0 auto' : '';
      }
    };

    const onMetadata = () => {
      if (el.videoHeight > 0) {
        aspectRatio = el.videoWidth / el.videoHeight;
        // Expose video aspect ratio for annotation overlay alignment
        wrapper.style.setProperty('--video-ar', `${el.videoWidth} / ${el.videoHeight}`);
        wrapper.style.setProperty('--video-ar-num', String(aspectRatio));
        // Sync the video element's CSS aspect-ratio to the actual playing
        // source (proxy). The React render sets it from PHP metadata (original
        // file dims), which may differ by a fraction. If the CSS and JS ratios
        // disagree, computeFit reads a height based on the CSS ratio then
        // multiplies by the JS ratio, producing a slightly different width each
        // frame — a ResizeObserver feedback loop that slowly shrinks the player.
        el.style.aspectRatio = `${el.videoWidth} / ${el.videoHeight}`;
      }
      computeFit();
    };

    computeFitRef.current = computeFit;

    el.addEventListener('loadedmetadata', onMetadata);
    // If metadata is already available, use it immediately.
    if (el.videoWidth > 0) onMetadata();
    // Otherwise run computeFit with the attribute-seeded aspect ratio so
    // the wrapper is sized correctly before loadedmetadata fires.
    else if (aspectRatio) computeFit();

    // Two separate observers with different callbacks so the annotation-sync
    // path (which reads layout) can't re-enter computeFit (which writes layout).
    //
    // computeFit observer — parent only.
    //   We must NOT observe the wrapper: computeFit writes wrapper.maxWidth,
    //   so observing the wrapper loops.
    //   We must NOT observe the video element: the video is `width: 100%` of
    //   the wrapper, so any wrapper width change resizes the video → looped
    //   computeFit even without observing the wrapper directly.
    //   The parent (.player-column) is driven only by external layout (viewport,
    //   flex siblings), so observing it is safe.
    //
    // Annotation-sync observer — video element only.
    //   Annotation roots need to follow the video's actual content box,
    //   including after computeFit shrinks it. syncAnnotationRoots is
    //   layout-read-only (it writes to annotation roots, not to the wrapper
    //   or video), so there is no feedback loop.
    let fitObserver: ResizeObserver | undefined;
    let annotationObserver: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      fitObserver = new ResizeObserver(() => computeFit());
      if (wrapper.parentElement) fitObserver.observe(wrapper.parentElement);

      annotationObserver = new ResizeObserver(() => syncAnnotationRoots());
      annotationObserver.observe(el);
    }

    // Watch for annotation roots being added/removed so syncAnnotationRoots
    // runs immediately when a viewer annotation is shown (not just on resize).
    // Also tracks whether an annotation editor is present so the overlay layer
    // can hide mattes/safe areas while drawing.
    let mutationObs: MutationObserver | undefined;
    if (typeof MutationObserver !== 'undefined') {
      mutationObs = new MutationObserver((mutations) => {
        let needsSync = false;
        let editorChanged = false;
        for (const m of mutations) {
          for (const node of Array.from(m.addedNodes)) {
            if (node instanceof HTMLElement && node.hasAttribute('data-annotation-root')) {
              needsSync = true;
              if (node.getAttribute('data-annotation-root') === 'editor') editorChanged = true;
            }
          }
          for (const node of Array.from(m.removedNodes)) {
            if (node instanceof HTMLElement && node.hasAttribute('data-annotation-root')) {
              if (node.getAttribute('data-annotation-root') === 'editor') editorChanged = true;
            }
          }
        }
        if (needsSync) syncAnnotationRoots();
        if (editorChanged) {
          setAnnotationEditorActive(!!wrapper.querySelector('[data-annotation-root="editor"]'));
        }
      });
      mutationObs.observe(wrapper, { childList: true });
    }

    return () => {
      el.removeEventListener('loadedmetadata', onMetadata);
      fitObserver?.disconnect();
      annotationObserver?.disconnect();
      mutationObs?.disconnect();
      computeFitRef.current = null;
      wrapper.style.maxWidth = '';
      wrapper.style.margin = '';
    };
  }, [mediaElementId, mediaElementRefProp, fullscreenTargetRefProp]);

  // Re-run computeFit when fullscreen state changes so the wrapper
  // contracts back to the video's edges after exiting fullscreen.
  // This is a separate effect because the main fit effect uses a ref for
  // isFullscreen to avoid tearing down observers on every toggle.
  useEffect(() => {
    computeFitRef.current?.();
  }, [state.isFullscreen]);

  // Playback error handling — codec detection, error events, URL retry
  const { errorState, setError: showError, clearError, skipNextRetry } = usePlaybackErrorHandler(
    mediaRef.current,
    fileId,
    linkId,
  );

  const { status: overlayStatus, show: showStatus } = useStatusOverlay();

  const { activeMatte, setActiveMatte, activeSafeAreas, toggleSafeArea } = useVideoOverlays();

  const isVideo = mediaType ? mediaType === 'video' : detectedIsVideo;

  // Audio always shows the waveform area (image optional); video requires waveformUrl + toggle
  const showWaveform = isVideo
    ? (waveformUrl ? state.waveformVisible : false)
    : true;

  // Subscriber list stored as a ref (not state) so subscribe/unsubscribe
  // take effect IMMEDIATELY — no React render cycle gap. With useState,
  // unsubscribing the old source and subscribing the new source during a
  // column switch left a gap where neither source's events reached the
  // sync handler, causing both columns to play independently for 1-2
  // render frames. The ref is read at call time by useMediaEvents,
  // usePlaybackShuttle, and handlePlayClick.
  const subscribersRef = useRef<PlayerEventCallback[]>([]);

  // Sync volume to media element and localStorage
  useEffect(() => {
    if (!mediaRef.current) return;
    mediaRef.current.volume = state.isMuted ? 0 : state.volume;
    try {
      localStorage.setItem('acornvolume', String(state.volume));
    } catch {
      // localStorage not available
    }
  }, [state.volume, state.isMuted]);

  // Sync loop to media element and localStorage
  useEffect(() => {
    if (mediaRef.current) {
      mediaRef.current.loop = state.isLooping;
    }
    try {
      localStorage.setItem('player.loop', String(state.isLooping));
    } catch {
      // localStorage not available
    }
  }, [state.isLooping]);

  // Sync playback settings to localStorage
  useEffect(() => {
    try { localStorage.setItem('player.settings.autoplay', String(state.autoplay)); } catch {}
  }, [state.autoplay]);

  useEffect(() => {
    try { localStorage.setItem('player.settings.showMarkers', String(state.showMarkers)); } catch {}
  }, [state.showMarkers]);

  useEffect(() => {
    try { localStorage.setItem('player.settings.showHeatmapsOnHover', String(state.showHeatmapsOnHover)); } catch {}
  }, [state.showHeatmapsOnHover]);

  useEffect(() => {
    try { localStorage.setItem('player.settings.pauseWhileTyping', String(state.pauseWhileTyping)); } catch {}
  }, [state.pauseWhileTyping]);

  useEffect(() => {
    try { localStorage.setItem('player.settings.autoCaptions', String(state.autoCaptions)); } catch {}
  }, [state.autoCaptions]);

  // Auto-enable captions when tracks become available
  useEffect(() => {
    if (state.autoCaptions && state.captionTracks.length > 0 && state.selectedTrackIdx === -1) {
      dispatch({ type: 'SELECT_TRACK', index: 0 });
    }
  }, [state.autoCaptions, state.captionTracks]);

  // Sync heatmap position to match the seek bar dimensions.
  // Uses a ResizeObserver so it updates when the player resizes.
  const seekBarRefCallback = useCallback((el: HTMLDivElement | null) => {
    seekBarElRef.current = el;
  }, []);

  useEffect(() => {
    const el = seekBarElRef.current;
    const container = containerRef.current;
    if (!el || !container || typeof ResizeObserver === 'undefined') return;

    const sync = () => {
      const barRect = el.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      setHeatmapRect({
        left: barRect.left - containerRect.left,
        width: barRect.width,
      });
    };

    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    ro.observe(container);
    return () => ro.disconnect();
  }, [seekBarElRef.current, containerRef.current]); // eslint-disable-line react-hooks/exhaustive-deps

  // Collect sources from media element
  const sources: MediaSource[] = useMemo(() => {
    if (!mediaRef.current) return [];
    const sourceEls = mediaRef.current.querySelectorAll('source');
    return Array.from(sourceEls).map((el, i) => ({
      src: el.src,
      type: el.type || (isVideo ? 'video/mp4' : 'audio/mpeg'),
      label: el.dataset.label || (i === 0 ? 'Proxy' : 'Original'),
      proxyId: el.dataset.proxyId || undefined,
    }));
    // Include fileId so a shallow reload (same media element, new sources) picks
    // up the new URLs when the parent re-renders via root.render() with new props.
  }, [state.isReady, isVideo, fileId]);

  // Media event bindings
  const callbacks = useMemo(
    () => ({ onTimeUpdate, onPlay, onPause, onEnded }),
    [onTimeUpdate, onPlay, onPause, onEnded],
  );
  useMediaEvents(mediaRef.current, dispatch, callbacks, subscribersRef, fps);

  // ── Synced Session bridging ────────────────────────────────────────────
  //
  // Leader: forward local PLAY / PAUSE / SEEK playback events to the
  // SyncedSessionManager so followers mirror them.
  //
  // Follower: subscribe to manager events and drive the media element.
  // Also forward media buffering events to KWAIT / KREADY so the leader's
  // FollowerReadyButton can show the correct composite state.
  //
  // `isApplyingRemoteRef` suppresses the echo loop: when a remote command
  // triggers a local play()/pause()/seek(), the browser fires its own
  // 'play'/'pause' events which would otherwise be re-broadcast.
  const isApplyingRemoteRef = useRef(false);
  useEffect(() => {
    if (!syncedSession?.enabled || !syncedSessionManager) return;
    const media = mediaRef.current;
    const unsubs: Array<() => void> = [];

    if (isLeader) {
      const broadcastTimecode = (currentTime: number) => {
        const tc = formatTimecode(currentTime, fps ?? 24, startFrame, dropFrame);
        // Truncate or pad to the 11-char wire format (HH:MM:SS:FF, 2-digit frames).
        const wire = tc.length >= 11 ? tc.slice(0, 11) : tc.padEnd(11, '0');
        syncedSessionManager.sendSeek(wire, true);
      };
      const subscriber: PlayerEventCallback = (event) => {
        if (isApplyingRemoteRef.current) return;
        switch (event.type) {
          case PlayerEventType.PLAY:
            // Broadcast the current timecode before KPLAY so followers
            // start at the leader's exact position rather than wherever
            // they had drifted to.
            broadcastTimecode(event.currentTime);
            syncedSessionManager.sendPlay();
            break;
          case PlayerEventType.PAUSE:
            // Same on pause — without the leading seek, followers pause
            // at their drifted local time instead of the leader's
            // position. Mirrors player_utils.js (updateTimecode(true,true)
            // then sendSocketString('KPAUSE')).
            broadcastTimecode(event.currentTime);
            syncedSessionManager.sendPause();
            break;
          case PlayerEventType.SEEK:
            broadcastTimecode(event.currentTime);
            break;
          case PlayerEventType.REWIND:
            syncedSessionManager.sendRewind();
            break;
          case PlayerEventType.FAST_FORWARD:
            syncedSessionManager.sendFastForward();
            break;
          default:
            break;
        }
      };
      subscribersRef.current = [...subscribersRef.current, subscriber];
      unsubs.push(() => {
        subscribersRef.current = subscribersRef.current.filter(s => s !== subscriber);
      });
    }

    if (isFollower) {
      // React to remote leader commands.
      unsubs.push(syncedSessionManager.on('play', () => {
        if (!media) return;
        isApplyingRemoteRef.current = true;
        // The follower page hasn't received a user gesture yet (the user
        // just navigated to the URL), so unmuted playback is blocked by
        // the browser's autoplay policy. Retry muted on rejection — the
        // user can unmute via the volume control. Mirrors the legacy
        // player_utils.js behavior at lines 391-398.
        const clearFlag = () => queueMicrotask(() => { isApplyingRemoteRef.current = false; });
        const result = media.play();
        if (result && typeof result.then === 'function') {
          result.then(clearFlag).catch(() => {
            try {
              media.muted = true;
              media.play().catch(() => { /* give up — user must press play */ }).finally(clearFlag);
            } catch {
              clearFlag();
            }
          });
        } else {
          clearFlag();
        }
      }));
      unsubs.push(syncedSessionManager.on('pause', () => {
        if (!media) return;
        isApplyingRemoteRef.current = true;
        try { media.pause(); } finally {
          queueMicrotask(() => { isApplyingRemoteRef.current = false; });
        }
      }));
      unsubs.push(syncedSessionManager.on('seek', (e) => {
        if (!media) return;
        const targetFrame = timecodeToFrameCount(e.timecode, fps ?? 24, dropFrame) - startFrame;
        const seconds = targetFrame / (fps ?? 24);
        if (!Number.isFinite(seconds)) return;
        isApplyingRemoteRef.current = true;
        try { media.currentTime = Math.max(0, seconds); } finally {
          queueMicrotask(() => { isApplyingRemoteRef.current = false; });
        }
      }));

    }

    // Forward local buffer events so the composite ready indicator stays
    // accurate. Both leader and follower do this — the server only emits
    // KALLREADY once every connected socket (leader included) has sent
    // KREADY, so without the leader's KREADY the indicator would never
    // turn green. Matches legacy player_utils.js (line ~1027/1039/1086)
    // where `sendSocketString('KREADY')` runs in 'seeked' / 'canplay' /
    // 'canplaythrough' listeners regardless of role.
    if (media) {
      const onWait = () => syncedSessionManager.sendWait();
      const onReady = () => syncedSessionManager.sendReady();
      media.addEventListener('waiting', onWait);
      media.addEventListener('seeking', onWait);
      media.addEventListener('canplay', onReady);
      media.addEventListener('canplaythrough', onReady);
      media.addEventListener('seeked', onReady);
      unsubs.push(() => {
        media.removeEventListener('waiting', onWait);
        media.removeEventListener('seeking', onWait);
        media.removeEventListener('canplay', onReady);
        media.removeEventListener('canplaythrough', onReady);
        media.removeEventListener('seeked', onReady);
      });

      // If the media is already ready by the time we wire up (common for
      // cached files where canplay fired before this effect ran), send
      // KREADY immediately so the server's ready set reflects reality.
      // HAVE_FUTURE_DATA = 3, HAVE_ENOUGH_DATA = 4.
      if (media.readyState >= 3) {
        syncedSessionManager.sendReady();
      }

      // If the WebSocket opens AFTER the media is already past 'canplay'
      // (the common case — media loads quickly, token fetch + WS handshake
      // takes longer), the canplay-driven sendReady fired into a closed
      // socket. Re-send on the next 'connect' so the server's ready set
      // is correct. Without this the server never receives the leader's
      // KREADY and KALLREADY never fires → indicator stuck on orange.
      unsubs.push(syncedSessionManager.on('connect', () => {
        if (media.readyState >= 3) {
          syncedSessionManager.sendReady();
        }
      }));
    }

    // Leader: also broadcast the current timecode on every native 'seeked'
    // event. shuttle.seekTo emits a SEEK event handled above, but other
    // seek paths (programmatic media.currentTime mutations, browser
    // restoration, etc.) don't go through shuttle. Mirroring the legacy
    // player_utils.js 'seeked' listener catches those.
    if (isLeader && media) {
      const onSeeked = () => {
        if (isApplyingRemoteRef.current) return;
        const tc = formatTimecode(media.currentTime, fps ?? 24, startFrame, dropFrame);
        const wire = tc.length >= 11 ? tc.slice(0, 11) : tc.padEnd(11, '0');
        syncedSessionManager.sendSeek(wire, true);
      };
      media.addEventListener('seeked', onSeeked);
      unsubs.push(() => media.removeEventListener('seeked', onSeeked));
    }

    return () => { unsubs.forEach(fn => fn()); };
  }, [syncedSession?.enabled, syncedSessionManager, isLeader, isFollower, state.isReady, fps, startFrame, dropFrame]);

  // Leader: periodic timecode resync during playback. The 'seeked' event
  // covers explicit jumps but does nothing for steady-state playback
  // drift (followers run their own clocks; over minutes they wander).
  // Broadcast an absolute timecode every 20 s while playing — matches the
  // `syncTimer` in player_utils.js (line 910).
  useEffect(() => {
    if (!syncedSession?.enabled || !syncedSessionManager) return;
    if (!isLeader || !state.isPlaying) return;
    const media = mediaRef.current;
    if (!media) return;
    const interval = setInterval(() => {
      if (isApplyingRemoteRef.current) return;
      const tc = formatTimecode(media.currentTime, fps ?? 24, startFrame, dropFrame);
      const wire = tc.length >= 11 ? tc.slice(0, 11) : tc.padEnd(11, '0');
      syncedSessionManager.sendSeek(wire, true);
    }, 20_000);
    return () => clearInterval(interval);
  }, [syncedSession?.enabled, syncedSessionManager, isLeader, state.isPlaying, fps, startFrame, dropFrame]);

  // Heatmap playback tracking — sends bucket data to the server
  useHeatmapTracker(trackHeatmap ? mediaRef.current : null, fileId, linkId);

  // Frame-accurate time updates during playback (RAF-based)
  useFrameAccurateTime(mediaRef.current, state.isPlaying, fps, dispatch);

  // Fullscreen — fullscreen the wrapper element (parent of video + controls).
  // The video element is passed as a fallback so iPhone Safari (where the
  // wrapper <div> has no fullscreen API) can use the iOS-native fullscreen
  // video player for true fullscreen — hides Safari's browser chrome — instead
  // of falling back to CSS pseudo-fullscreen.
  const { toggleFullscreen, enterFullscreen, exitFullscreen } = useFullscreen(
    isVideo ? fullscreenTargetRef.current : null,
    dispatch,
    onFullscreenChange,
    isVideo && mediaRef.current instanceof HTMLVideoElement ? mediaRef.current : null,
  );

  // Guard: check onBeforeSeek before allowing any seek operation.
  // After a cancellation, suppress further checks briefly to avoid
  // duplicate dialogs from React reconciling the range input value.
  const seekCancelledRef = useRef(false);
  const checkBeforeSeek = useCallback(() => {
    if (seekCancelledRef.current) return false;
    if (onBeforeSeek && !onBeforeSeek()) {
      seekCancelledRef.current = true;
      setTimeout(() => { seekCancelledRef.current = false; }, 100);
      return false;
    }
    return true;
  }, [onBeforeSeek]);

  // Playback shuttle — rewind / fast-forward / slow-step state and commands,
  // plus the central `seekTo` helper that every internal currentTime-changing
  // site routes through so a SEEK event is always emitted for follower
  // columns. Declared here so marker nav and other useCallbacks below can
  // reference `shuttle.seekTo`. Wired into both KollabPlayerAPI (via
  // useImperativeHandle below) and the keyboard handler.
  const shuttle = usePlaybackShuttle({
    // Pass the REF object, not its current value. The hook reads
    // mediaRef.current at call time so we're not stuck with a stale
    // null closure from the first render (mediaRef.current is assigned
    // inside a useEffect that runs after commit).
    mediaRef,
    fps: fps ?? 0,
    onSeek,
    onShowStatus: showStatus,
    subscribersRef,
  });

  // ── Synced Session bridging (part 2: shuttle-dependent commands) ───────
  //
  // Split from the play/pause/seek effect above because these handlers need
  // `shuttle`, which is created below the synced-session import order. Covers:
  //   - Follower: KREW / KFF / KHOME / KFILE
  //   - Leader:   KTCREQ response, KCONNECT (new peer joined) catch-up
  useEffect(() => {
    if (!syncedSession?.enabled || !syncedSessionManager) return;
    const unsubs: Array<() => void> = [];

    if (isFollower) {
      unsubs.push(syncedSessionManager.on('rewind', () => {
        isApplyingRemoteRef.current = true;
        try { shuttle.rewind(); } finally {
          queueMicrotask(() => { isApplyingRemoteRef.current = false; });
        }
      }));
      unsubs.push(syncedSessionManager.on('fastForward', () => {
        isApplyingRemoteRef.current = true;
        try { shuttle.fastForward(); } finally {
          queueMicrotask(() => { isApplyingRemoteRef.current = false; });
        }
      }));
      unsubs.push(syncedSessionManager.on('home', () => {
        const media = mediaRef.current;
        if (!media) return;
        isApplyingRemoteRef.current = true;
        try {
          media.pause();
          media.currentTime = 0;
        } finally {
          queueMicrotask(() => { isApplyingRemoteRef.current = false; });
        }
      }));
      unsubs.push(syncedSessionManager.on('fileChange', (e) => {
        // Hard navigation matches the legacy player_utils.js behavior; the
        // React app has no SPA router for the player page yet.
        if (e.fileId !== fileId) {
          window.location.href = '/player?id=' + e.fileId;
        }
      }));
    }

    if (isLeader) {
      const broadcastCurrentTimecode = () => {
        const media = mediaRef.current;
        if (!media) return;
        const tc = formatTimecode(media.currentTime, fps ?? 24, startFrame, dropFrame);
        const wire = tc.length >= 11 ? tc.slice(0, 11) : tc.padEnd(11, '0');
        syncedSessionManager.sendSeek(wire, true);
      };
      unsubs.push(syncedSessionManager.on('tcreq', broadcastCurrentTimecode));
      unsubs.push(syncedSessionManager.on('peerConnect', () => {
        // New peer joined the room — re-broadcast file, current TC, and
        // play state so they catch up.
        if (typeof fileId === 'number' && fileId > 0) {
          syncedSessionManager.sendFileChange(fileId);
        }
        broadcastCurrentTimecode();
        if (state.isPlaying) syncedSessionManager.sendPlay();
        // Also re-affirm our ready state. The server's `sessionBuffered`
        // tracks per-socket KREADY; if our initial KREADY raced the WS
        // open and was dropped, the server still won't have us in the
        // ready set when this new peer triggers a KALLREADY check.
        const media = mediaRef.current;
        if (media && media.readyState >= 3) {
          syncedSessionManager.sendReady();
        }
      }));
    }

    return () => { unsubs.forEach(fn => fn()); };
  }, [syncedSession?.enabled, syncedSessionManager, isLeader, isFollower, shuttle, fileId, fps, startFrame, dropFrame, state.isPlaying]);

  // Marker navigation — standalone callbacks reused by both the keyboard
  // nav hook and the imperative API exposed via ref.
  const seekToNextMarker = useCallback(() => {
    if (!checkBeforeSeek()) return;
    const time = mediaRef.current?.currentTime ?? 0;
    const sorted = comments
      .filter((c) => c.reply <= 0 && !isNaN(c.pos))
      .sort((a, b) => a.pos - b.pos);
    const next = sorted.find((c) => c.pos > time + 0.1);
    if (next) {
      shuttle.seekTo(next.pos);
    }
  }, [comments, checkBeforeSeek, shuttle]);

  const seekToPreviousMarker = useCallback(() => {
    if (!checkBeforeSeek()) return;
    const time = mediaRef.current?.currentTime ?? 0;
    const sorted = comments
      .filter((c) => c.reply <= 0 && !isNaN(c.pos))
      .sort((a, b) => b.pos - a.pos);
    const prev = sorted.find((c) => c.pos < time - 0.1);
    if (prev) {
      shuttle.seekTo(prev.pos);
    }
  }, [comments, checkBeforeSeek, shuttle]);

  const handleMuteToggle = useCallback(() => {
    dispatch({ type: 'TOGGLE_MUTE' });
    showStatus(state.isMuted ? 'Unmuted' : 'Muted');
  }, [state.isMuted, showStatus]);

  // Keyboard navigation (delegates to `shuttle` declared above)
  useKeyboardNav({
    mediaEl: mediaRef.current,
    containerEl: containerRef.current,
    dispatch,
    fps: fps ?? 0,
    onSeek,
    onBeforeSeek,
    onFullscreenToggle: isVideo ? toggleFullscreen : undefined,
    onMuteToggle: handleMuteToggle,
    onNextMarker: seekToNextMarker,
    onPrevMarker: seekToPreviousMarker,
    onShowStatus: showStatus,
    basePlaybackRateRef: userPlaybackRateRef,
    subscribersRef,
    rewind: shuttle.rewind,
    fastForward: shuttle.fastForward,
    startSlowStepBack: shuttle.startSlowStepBack,
    startSlowStepForward: shuttle.startSlowStepForward,
    stopSlowStep: shuttle.stopSlowStep,
    clearRewind: shuttle.clearRewind,
    clearSlowStep: shuttle.clearSlowStep,
    incrementFrames: shuttle.incrementFrames,
    seekTo: shuttle.seekTo,
    isFollower,
  });

  // Captions
  const { loadTrack } = useCaptions(
    mediaRef.current,
    dispatch,
    state.selectedTrackIdx,
    state.captions,
    state.currentTime,
    fps,
  );

  // Caption speech synthesis
  const speechSupported = useMemo(() => isSpeechSynthesisSupported(), []);
  const { speechEnabled, setSpeechEnabled, showSettingsDialog: showSpeechSettings } = useCaptionSpeech(
    state.activeCueText,
    state.activeCueEnd,
    state.isPlaying,
    state.isSeeking,
    state.currentTime,
    mediaRef.current,
    isFollower,
  );

  const handleToggleSpeech = useCallback(() => {
    setSpeechEnabled(!speechEnabled);
  }, [speechEnabled, setSpeechEnabled]);

  const handleOpenSpeechSettings = useCallback(() => {
    dispatch({ type: 'TOGGLE_CAPTION_PICKER' });
    showSpeechSettings();
  }, [showSpeechSettings]);

  // Load caption track when selection changes
  useEffect(() => {
    if (state.selectedTrackIdx >= 0 && state.captionTracks[state.selectedTrackIdx]) {
      loadTrack(state.captionTracks[state.selectedTrackIdx]);
    }
  }, [state.selectedTrackIdx, state.captionTracks, loadTrack]);

  // Seek to startTime once the media is ready.
  useEffect(() => {
    if (startTime && startTime > 0 && mediaRef.current && state.isReady) {
      shuttle.seekTo(startTime);
      dispatch({ type: 'SEEK_END', time: startTime });
    }
  }, [state.isReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // Autoplay — respects both the prop and the user's settings toggle.
  // In a synced session BOTH roles skip autoplay: the leader is expected
  // to start playback manually so they're choosing when followers should
  // start; the follower waits for the leader's KPLAY (or peerConnect
  // catch-up). Autoplaying either side races the explicit sync state.
  //
  // Multi-column awareness: when a parent (column manager) provides
  // `onMediaReady`, we report ready state up instead of immediately
  // calling play(). The manager can then synchronize the start across
  // columns so the first one doesn't run ahead of the second on page
  // load.
  useEffect(() => {
    if (syncedSession?.enabled) return;
    if (!mediaRef.current || !state.isReady) return;
    const myMedia = mediaRef.current;
    const wantsAutoplay = !!(autoplay && state.autoplay);
    const playFn = wantsAutoplay
      ? () => {
          myMedia.play().catch((err: Error) => {
            console.warn('Autoplay blocked by browser:', err.message);
          });
        }
      : undefined;
    if (onMediaReadyRef.current) {
      onMediaReadyRef.current(playFn);
    } else if (playFn) {
      playFn();
    }
  }, [autoplay, state.autoplay, state.isReady, syncedSession?.enabled]);

  // Remind user of non-1x playback speed when playback starts
  useEffect(() => {
    if (state.isPlaying && userPlaybackRateRef.current !== 1.0) {
      showStatus(`${userPlaybackRateRef.current}x`);
    }
  }, [state.isPlaying, showStatus]);

  // Auto-hide controls in fullscreen
  const resetHideTimer = useCallback(() => {
    if (!state.isFullscreen) return; // No-op outside fullscreen — avoids re-renders on every mousemove
    dispatch({ type: 'SET_CONTROLS_VISIBLE', visible: true });
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (!isOverControlsRef.current) {
      hideTimerRef.current = setTimeout(() => {
        dispatch({ type: 'SET_CONTROLS_VISIBLE', visible: false });
      }, 3000);
    }
  }, [state.isFullscreen]);

  // Keep controls visible while mouse is over them
  const handleControlsMouseEnter = useCallback(() => {
    isOverControlsRef.current = true;
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  }, []);

  const handleControlsMouseLeave = useCallback(() => {
    isOverControlsRef.current = false;
    if (state.isFullscreen) {
      hideTimerRef.current = setTimeout(() => {
        dispatch({ type: 'SET_CONTROLS_VISIBLE', visible: false });
      }, 3000);
    }
  }, [state.isFullscreen]);

  // In fullscreen, attach mousemove to the wrapper element so movement over
  // the video area (outside the React tree) still shows/hides controls
  useEffect(() => {
    const wrapper = fullscreenTargetRef.current;
    if (!state.isFullscreen || !wrapper) return;

    const handleWrapperMouseMove = () => resetHideTimer();
    wrapper.addEventListener('mousemove', handleWrapperMouseMove);
    return () => {
      wrapper.removeEventListener('mousemove', handleWrapperMouseMove);
    };
  }, [state.isFullscreen, resetHideTimer]);

  // Ensure controls are visible when leaving fullscreen — cancel any
  // pending hide timeout that was set while in fullscreen mode.
  // Also restore any adopted side panel element to its original position.
  useEffect(() => {
    if (!state.isFullscreen) {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      dispatch({ type: 'SET_CONTROLS_VISIBLE', visible: true });
      dispatch({ type: 'SET_SIDE_PANEL', open: false });

      // Restore adopted element to its original DOM position.
      // Uses the direct element ref (not panel.firstChild) because the
      // SidePanel portal may already be unmounted when this effect fires.
      const origin = sidePanelElementOriginRef.current;
      const el = sidePanelAdoptedElRef.current;
      if (origin && el) {
        if (origin.nextSibling) {
          origin.parent.insertBefore(el, origin.nextSibling);
        } else {
          origin.parent.appendChild(el);
        }
        sidePanelElementOriginRef.current = null;
        sidePanelAdoptedElRef.current = null;
      }
    }
  }, [state.isFullscreen]);

  // Sync sidePanelWidth prop to state
  useEffect(() => {
    if (sidePanelWidthProp != null && sidePanelWidthProp > 0) {
      dispatch({ type: 'SET_SIDE_PANEL_WIDTH', width: sidePanelWidthProp });
    }
  }, [sidePanelWidthProp]);

  // Toggle wrapper class to reserve space for the side panel when open.
  // The class adds padding-right so the video shrinks (object-fit: contain
  // letterboxes naturally) instead of the panel overlapping the video.
  // Also sets --side-panel-w CSS custom property for the panel width.
  useEffect(() => {
    const wrapper = fullscreenTargetRef.current;
    if (!wrapper) return;
    wrapper.style.setProperty('--side-panel-w', `${state.sidePanelWidth}px`);
    if (state.isFullscreen && state.sidePanelOpen) {
      wrapper.classList.add('kollab-player-sidepanel-active');
    } else {
      wrapper.classList.remove('kollab-player-sidepanel-active');
    }

    // Hide annotation roots during the sidebar transition, then show
    // them in the correct position after the animation completes.
    const roots = wrapper.querySelectorAll('[data-annotation-root]');
    roots.forEach((r) => (r as HTMLElement).style.visibility = 'hidden');
    const timerId = setTimeout(() => {
      syncAnnotationRootsRef.current?.();
      roots.forEach((r) => (r as HTMLElement).style.visibility = '');
    }, 320); // slightly longer than the 300ms CSS transition
    return () => clearTimeout(timerId);
  }, [state.isFullscreen, state.sidePanelOpen, state.sidePanelWidth]);

  // Toggle wrapper class when waveform is visible
  useEffect(() => {
    const wrapper = fullscreenTargetRef.current;
    if (!wrapper) return;
    if (showWaveform) {
      wrapper.classList.add('kollab-player-waveform-active');
    } else {
      wrapper.classList.remove('kollab-player-waveform-active');
    }
  }, [showWaveform]);

  // Give the wrapper a real minimum height when an audio playback error is
  // shown. The <audio> element renders with no height (controls removed), so
  // the wrapper collapses to the control-bar height. The error overlay is
  // portalled into the wrapper as position:absolute with container-type:size,
  // so without this its cqh-based font sizes clamp to their minimums and the
  // message is cut off. Video players already have a tall video area, so this
  // only applies to audio.
  useEffect(() => {
    const wrapper = fullscreenTargetRef.current;
    if (!wrapper) return;
    if (!isVideo && errorState.hasError) {
      wrapper.classList.add('kollab-player-wrapper--audio-error');
    } else {
      wrapper.classList.remove('kollab-player-wrapper--audio-error');
    }
  }, [isVideo, errorState.hasError]);

  // Helper: adopt a DOM element into the side panel (or restore the current one).
  // Sets hasElement immediately so the SidePanel renders (making the ref available).
  // If the panel ref isn't ready yet, the physical move is deferred to a retry effect.
  const adoptSidePanelElement = useCallback((elementId: string | null) => {
    const panel = sidePanelContentRef.current;

    // Restore any previously adopted element first
    const origin = sidePanelElementOriginRef.current;
    const adopted = sidePanelAdoptedElRef.current;
    if (origin && adopted) {
      if (origin.nextSibling) {
        origin.parent.insertBefore(adopted, origin.nextSibling);
      } else {
        origin.parent.appendChild(adopted);
      }
      sidePanelElementOriginRef.current = null;
      sidePanelAdoptedElRef.current = null;
    }

    if (!elementId) {
      dispatch({ type: 'SET_SIDE_PANEL_HAS_ELEMENT', hasElement: false });
      return;
    }

    // Signal intent so the SidePanel renders (toggle button becomes visible)
    dispatch({ type: 'SET_SIDE_PANEL_HAS_ELEMENT', hasElement: true });

    if (!panel) return; // Panel not rendered yet; will retry when fullscreen activates

    const el = document.getElementById(elementId);
    if (!el || !el.parentElement) return;

    // Already adopted (e.g. from a previous effect run)
    if (el.parentElement === panel) return;

    // Record original position for restoration on fullscreen exit
    sidePanelElementOriginRef.current = {
      parent: el.parentElement,
      nextSibling: el.nextSibling,
    };
    sidePanelAdoptedElRef.current = el;
    panel.appendChild(el);
  }, []);

  // Sync sidePanelElementId prop — adopt/restore when the prop changes or
  // when entering fullscreen (the panel div may not have existed on first attempt)
  useEffect(() => {
    if (state.isFullscreen) {
      adoptSidePanelElement(sidePanelElementId ?? null);
    }
  }, [sidePanelElementId, state.isFullscreen, adoptSidePanelElement]);

  // Expose API via ref for external code (supports multiple players)
  useImperativeHandle(ref, () => ({
    play: () => { mediaRef.current?.play(); },
    pause: () => { mediaRef.current?.pause(); },
    seekToTime: (time: number) => {
      // Delegate to the central shuttle.seekTo helper. It sets currentTime
      // (clamped), fires onSeek, and emits the SEEK event to subscribers.
      // We additionally dispatch SEEK_END for the reducer's internal state.
      shuttle.seekTo(time);
      dispatch({ type: 'SEEK_END', time });
    },
    isPlaying: () => !mediaRef.current?.paused,
    getPlaybackRate: () => mediaRef.current?.playbackRate ?? 1,
    setPlaybackRate: (rate: number) => {
      if (mediaRef.current && mediaRef.current.playbackRate !== rate) {
        userPlaybackRateRef.current = rate;
        if (mediaRef.current) mediaRef.current.playbackRate = rate;
        dispatch({type: 'SET_PLAYBACK_RATE', rate});
        showStatus(`${rate}x`);
      }
    },
    // Sync-handler rate overlay — see the doc comment on the API
    // interface. Only mutates the media element's playbackRate. Deliberately
    // skips userPlaybackRateRef (keeps the user's chosen rate intact for
    // handlePlayClick to restore on resume), skips the reducer dispatch
    // (keeps the controls' displayed rate showing the user's choice, not
    // the overlay), and skips the OSD flash.
    setSyncPlaybackRate: (rate: number) => {
      if (mediaRef.current && mediaRef.current.playbackRate !== rate) {
        mediaRef.current.playbackRate = rate;
      }
    },
    clearSyncPlaybackRate: () => {
      if (mediaRef.current && mediaRef.current.playbackRate !== userPlaybackRateRef.current) {
        mediaRef.current.playbackRate = userPlaybackRateRef.current;
      }
    },
    getCurrentTime: () => mediaRef.current?.currentTime ?? 0,
    getDuration: () => mediaRef.current?.duration ?? 0,
    getMediaElement: () => mediaRef.current,
    getContainerElement: () => containerRef.current,
    updateComments: (newComments: CommentMarker[]) => {
      setComments((prev) => {
        if (prev.length === newComments.length && prev.every((c, i) => c.id === newComments[i].id && c.pos === newComments[i].pos && c.color === newComments[i].color)) return prev;
        return newComments;
      });
    },
    showDownloadOverlay: (message?: string) => {
      showError(message || 'This file cannot be played.');
    },
    seekToNextMarker,
    seekToPreviousMarker,
    getKeyboardShortcuts: () => KEYBOARD_SHORTCUTS,
    captureFrame: (options?: FrameExportOptions) => {
      const video = mediaRef.current;
      if (!video || !(video instanceof HTMLVideoElement)) {
        return Promise.reject(new Error('No video element available'));
      }
      return captureFrame(video, options);
    },
    focus: (options?: FocusOptions) => { containerRef.current?.focus(options); },
    isFullscreen: () => isFullscreenRef.current,
    enterFullscreen,
    exitFullscreen,
    setSidePanelOpen: (open: boolean) => { dispatch({ type: 'SET_SIDE_PANEL', open }); },
    setSidePanelElement: (elementId: string | null) => {
      adoptSidePanelElement(elementId);
    },
    setSidePanelWidth: (width: number) => {
      dispatch({ type: 'SET_SIDE_PANEL_WIDTH', width });
    },
    selectCaptionLanguage: (lang: string | null) => {
      if (!lang) {
        dispatch({ type: 'SELECT_TRACK', index: -1 });
        return;
      }
      const idx = state.captionTracks.findIndex(
        (t) => t.srclang.toLowerCase() === lang.toLowerCase(),
      );
      if (idx >= 0) {
        dispatch({ type: 'SELECT_TRACK', index: idx });
      }
    },
    setWaveformVisible: (visible: boolean) =>
      dispatch({ type: 'SET_WAVEFORM_VISIBLE', visible }),
    getPauseWhileTyping: () => state.pauseWhileTyping,
    getAutoplay: () => state.autoplay,
    getShowMarkers: () => state.showMarkers,
    getShowHeatmapsOnHover: () => state.showHeatmapsOnHover,
    getAutoCaptions: () => state.autoCaptions,
    setMuted: (muted: boolean) => {
      // Set volume to 0 IMMEDIATELY when muting so the audio
      // stops synchronously. Without this, the dispatch updates
      // React state but the useEffect that actually writes
      // mediaEl.volume runs after the next render — on source
      // switches the old column's audio keeps playing in the gap,
      // causing audible doubled dialogue. Unmuting is less
      // critical — the useEffect handles it on the next render.
      if (muted && mediaRef.current) {
        mediaRef.current.volume = 0;
      }
      dispatch({ type: muted ? 'MUTE' : 'UNMUTE' });
    },
    subscribeToPlaybackEvents: (cb: PlayerEventCallback) =>  {
        subscribersRef.current = [...subscribersRef.current, cb];
        return { unsubscribe: () => { subscribersRef.current = subscribersRef.current.filter(item => item !== cb); } };
    },
    getFrameRate: () => props.fps ?? 0,
    rewind: shuttle.rewind,
    fastForward: shuttle.fastForward,
    startSlowStepBack: shuttle.startSlowStepBack,
    startSlowStepForward: shuttle.startSlowStepForward,
    stopSlowStep: shuttle.stopSlowStep,
    clearRewind: shuttle.clearRewind,
  }), [onSeek, checkBeforeSeek, showError, comments, enterFullscreen, exitFullscreen, adoptSidePanelElement, state.captionTracks, state.pauseWhileTyping, state.autoplay, state.showMarkers, state.showHeatmapsOnHover, state.autoCaptions, shuttle]);

  // Fire onMounted callback once after the first render commit.
  useEffect(() => { onMounted?.(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Unmount cleanup — restore any adopted side panel element (e.g. #comment-tabs)
  // to its original DOM position BEFORE React tears down the portal that
  // currently contains it. Without this, unmounting the player while fullscreen
  // (e.g. a dynamic file switch via loadFile) would leave the adopted element
  // inside the portal, where it gets destroyed when the portal DOM is removed.
  //
  // This effect has [] deps so its cleanup only runs on unmount — and since
  // it's the LAST effect declared in the component, its cleanup runs FIRST
  // in the reverse-order cleanup sequence, before any portal containers are
  // torn down.
  useEffect(() => {
    return () => {
      const origin = sidePanelElementOriginRef.current;
      const el = sidePanelAdoptedElRef.current;
      if (origin && el) {
        if (origin.nextSibling && origin.nextSibling.parentNode === origin.parent) {
          origin.parent.insertBefore(el, origin.nextSibling);
        } else {
          origin.parent.appendChild(el);
        }
        sidePanelElementOriginRef.current = null;
        sidePanelAdoptedElRef.current = null;
      }
    };
  }, []);

  // Sync comments from props
  useEffect(() => {
    if (initialComments) {
      setComments(initialComments);
    }
  }, [initialComments]);

  // Event handlers
  const handlePlayClick = useCallback(() => {
    if (!mediaRef.current) return;
    // Followers must not control playback — the leader drives it.
    if (isFollower) return;
    if (mediaRef.current.paused) {
      if (!checkBeforeSeek()) return;
      mediaRef.current.playbackRate = userPlaybackRateRef.current;

      // Pre-notify subscribers BEFORE calling play() on the source.
      // Without this, the source starts advancing immediately on play(),
      // but followers don't hear about it until the browser fires the
      // async 'play' event → useMediaEvents.onPlay → subscriber loop →
      // follower.play(). That 50-100 ms gap is the initial drift users
      // hear on every resume-from-pause. By notifying first, followers
      // call play() before the source, and both start advancing within
      // the same JS task. The duplicate PLAY notification that arrives
      // later from the browser event is harmless — the sync handler's
      // guard `!player.api.isPlaying()` suppresses it since the
      // follower is already playing by then.
      for (const sub of subscribersRef.current) {
        sub({
          type: PlayerEventType.PLAY,
          currentTime: mediaRef.current.currentTime,
          playbackRate: userPlaybackRateRef.current,
          isPaused: false,
        });
      }

      mediaRef.current.play();
    } else {
      mediaRef.current.pause();
    }
  }, [checkBeforeSeek, isFollower]);

  // Click-to-play for video elements — also focus the container so
  // keyboard shortcuts work without having to click a control first.
  // In multi-column mode (isActiveProp === false), the first click on
  // an inactive column's video should ONLY activate the column (via the
  // parent's onActivate on .player-stage), NOT toggle play/pause. The
  // activation click bubbles up naturally; we just skip handlePlayClick.
  // Single-column mode (isActiveProp defaults to true) preserves the
  // immediate click-to-play behavior.
  //
  // isActiveProp is read from a ref (not captured in the closure) so
  // that switching active columns doesn't tear down / re-add the click
  // listener on every column. That re-registration caused clicks to be
  // missed during the teardown gap. The ref is always current because
  // React updates it synchronously during render, before any events
  // can fire.
  const isActiveRef = useRef(isActiveProp);
  isActiveRef.current = isActiveProp;

  useEffect(() => {
    const el = mediaRef.current;
    if (!el || !isVideo) return;

    // Touch devices have no separate hover state, so the desktop
    // "first click activates, second click plays" pattern doubles the
    // taps required to start playback. On phones with dual videos open
    // this is awkward — collapse the activation tap into a play tap.
    const isTouchPrimary = typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(hover: none) and (pointer: coarse)').matches;

    const handleClick = () => {
      // Always focus the container so keyboard shortcuts work — even
      // on the activation click (when isActiveRef.current is still
      // false). Without this, activating a column by clicking the
      // video left the container unfocused, causing keyboard shortcuts
      // to not fire until a second click.
      containerRef.current?.focus();
      if (!isActiveRef.current && !isTouchPrimary) return; // activation only on desktop
      handlePlayClick();
    };

    const preventContextMenu = (e: Event) => e.preventDefault();

    el.addEventListener('click', handleClick);
    el.addEventListener('contextmenu', preventContextMenu);
    return () => {
      el.removeEventListener('click', handleClick);
      el.removeEventListener('contextmenu', preventContextMenu);
    };
    // state.isReady is in the deps so the effect re-runs after the media
    // element fires loadedmetadata (MEDIA_READY). On first render,
    // mediaRef.current is null (assigned in a later effect), so `el` is
    // null and the effect returns early. Once MEDIA_READY fires,
    // state.isReady flips to true → re-render → effect re-runs → el is
    // now the real video element → click handler registered.
  }, [isVideo, handlePlayClick, state.isReady]);

  const handleSkipBack = useCallback(() => {
    if (!mediaRef.current) return;
    if (!checkBeforeSeek()) return;
    shuttle.seekTo(mediaRef.current.currentTime - 5);
  }, [checkBeforeSeek, shuttle]);

  const handleSkipForward = useCallback(() => {
    if (!mediaRef.current) return;
    if (!checkBeforeSeek()) return;
    shuttle.seekTo(mediaRef.current.currentTime + 5);
  }, [checkBeforeSeek, shuttle]);

  const handleVolumeChange = useCallback(
    (volume: number) => {
      dispatch({ type: 'SET_VOLUME', volume });
      if (mediaRef.current) mediaRef.current.volume = volume;
    },
    [],
  );

  const handleSeekStart = useCallback(() => {
    dispatch({ type: 'SEEK_START' });

    // Dismiss annotation viewers when the user manually seeks via the
    // timeline. The viewer normally only auto-dismisses on 'play', so
    // seeking while paused leaves the annotation on screen.
    // Dispatch a custom event that mountAnnotationViewer listens for.
    // Programmatic seeks (e.g. seeking to a comment timecode) don't go
    // through the SeekBar, so they won't trigger this.
    mediaRef.current?.dispatchEvent(new Event('kollab:userseek'));
  }, []);

  const handleSeekEnd = useCallback(
    (time: number) => {
      // Followers cannot drive the seek bar — the leader owns the timeline.
      if (isFollower) return;
      if (!checkBeforeSeek()) return;
      // Delegate to shuttle.seekTo so clamping, onSeek, and SEEK event
      // emission happen via the central helper. Dispatch SEEK_END here
      // for the reducer's internal seeking-state tracking.
      shuttle.seekTo(time);
      dispatch({ type: 'SEEK_END', time });
    },
    [checkBeforeSeek, shuttle, isFollower],
  );

  const handleSpeedChange = useCallback((rate: number) => {
    userPlaybackRateRef.current = rate;
    if (mediaRef.current) mediaRef.current.playbackRate = rate;
    dispatch({ type: 'SET_PLAYBACK_RATE', rate });
    showStatus(`${rate}x`);

    // Send playback event to subscribers
    const el = mediaRef.current;
    if (el) {
      for (const subscriber of subscribersRef.current) {
        subscriber({ type: PlayerEventType.SPEED_CHANGE, currentTime: el.currentTime, playbackRate: el.playbackRate, isPaused: el.paused });
      }
    }
  }, [showStatus]);

  const handleSourceChange = useCallback(
    (index: number) => {
      if (!mediaRef.current || !sources[index]) return;
      const wasPlaying = !mediaRef.current.paused;
      const savedTime = mediaRef.current.currentTime;

      clearError();
      // Prevent the error handler from silently retrying with a different
      // URL — the user explicitly chose this source. If it fails, the
      // error overlay will appear with an option to switch back.
      skipNextRetry();
      mediaRef.current.pause();
      mediaRef.current.src = sources[index].src;
      mediaRef.current.load();

      const onLoaded = () => {
        mediaRef.current!.removeEventListener('loadedmetadata', onLoaded);

        // Detect partially-playable files: the browser can decode audio
        // but not the video codec (e.g. ProRes in a MOV container).
        // videoWidth is 0 when no video frames can be decoded.
        // Only flag this for video/* MIME types — audio-only files in a
        // QuickTime container legitimately have videoWidth === 0.
        const sourceType = sources[index].type.split(';')[0].trim();
        if (
          mediaRef.current instanceof HTMLVideoElement &&
          mediaRef.current.videoWidth === 0 &&
          sourceType.startsWith('video/')
        ) {
          mediaRef.current.pause();
          showError('This file cannot be played in your browser.', true);
          return;
        }

        shuttle.seekTo(savedTime);
        if (wasPlaying) mediaRef.current!.play();
      };
      mediaRef.current.addEventListener('loadedmetadata', onLoaded);

      dispatch({ type: 'SWITCH_SOURCE', index });
    },
    [sources, clearError, skipNextRetry, showError, shuttle],
  );

  const handleCaptionSelect = useCallback(
    (index: number) => {
      dispatch({ type: 'SELECT_TRACK', index });
    },
    [],
  );

  const handleMarkerSeek = useCallback(
    (time: number, commentId: number) => {
      if (!checkBeforeSeek()) return;
      dispatch({ type: 'SEEK_START' });
      // Delegate to shuttle.seekTo for clamping, onSeek, and SEEK emission.
      shuttle.seekTo(time);
      dispatch({ type: 'SEEK_END', time });
      // Switch comment source if needed and scroll to the comment
      if (commentId > 0 && typeof (window as any).switchCommentSourceAndScroll === 'function') {
        (window as any).switchCommentSourceAndScroll(fileId, commentId);
      }
    },
    [checkBeforeSeek, shuttle, fileId],
  );

  // Stable callbacks for menu toggles (avoids inline lambdas in JSX
  // which would defeat React.memo on child components)
  const handleSeekBarHover = useCallback((hovering: boolean) => {
    setSeekBarHovered(hovering);
  }, []);

  const handleToggleLoop = useCallback(() => {
    dispatch({ type: 'TOGGLE_LOOP' });
  }, []);

  const handleToggleCaptionPicker = useCallback(() => {
    dispatch({ type: 'TOGGLE_CAPTION_PICKER' });
  }, []);

  const handleToggleActionsMenu = useCallback(() => {
    dispatch({ type: 'TOGGLE_ACTIONS_MENU' });
  }, []);

  const handleToggleTimecodePopover = useCallback(() => {
    dispatch({ type: 'TOGGLE_TIMECODE_POPOVER' });
  }, []);

  const handleToggleAutoplay = useCallback(() => {
    dispatch({ type: 'TOGGLE_AUTOPLAY' });
  }, []);

  const handleToggleShowMarkers = useCallback(() => {
    dispatch({ type: 'TOGGLE_SHOW_MARKERS' });
  }, []);

  const handleToggleShowHeatmapsOnHover = useCallback(() => {
    dispatch({ type: 'TOGGLE_SHOW_HEATMAPS_ON_HOVER' });
  }, []);

  const handleTogglePauseWhileTyping = useCallback(() => {
    dispatch({ type: 'TOGGLE_PAUSE_WHILE_TYPING' });
  }, []);

  const handleOpenSettings = useCallback(() => {
    showPlaybackSettingsDialog(
      {
        autoplay: state.autoplay,
        showMarkers: state.showMarkers,
        showHeatmapsOnHover: state.showHeatmapsOnHover,
        pauseWhileTyping: state.pauseWhileTyping,
        autoCaptions: state.autoCaptions,
      },
      (key) => {
        if (key === 'autoplay') dispatch({ type: 'TOGGLE_AUTOPLAY' });
        else if (key === 'showMarkers') dispatch({ type: 'TOGGLE_SHOW_MARKERS' });
        else if (key === 'showHeatmapsOnHover') dispatch({ type: 'TOGGLE_SHOW_HEATMAPS_ON_HOVER' });
        else if (key === 'pauseWhileTyping') dispatch({ type: 'TOGGLE_PAUSE_WHILE_TYPING' });
        else if (key === 'autoCaptions') dispatch({ type: 'TOGGLE_AUTO_CAPTIONS' });
      },
    );
  }, [state.autoplay, state.showMarkers, state.showHeatmapsOnHover, state.pauseWhileTyping, state.autoCaptions]);

  // Simplified captions toggle for overflow menu (select first track or turn off)
  const handleCaptionsOverflowToggle = useCallback(() => {
    if (state.selectedTrackIdx >= 0) {
      dispatch({ type: 'SELECT_TRACK', index: -1 });
    } else if (state.captionTracks.length > 0) {
      dispatch({ type: 'SELECT_TRACK', index: 0 });
    }
  }, [state.selectedTrackIdx, state.captionTracks.length]);

  // --- Responsive button overflow ---
  // Buttons that can be collapsed into the Actions menu, ordered by priority
  // (highest priority = stays visible longest, collapses last)
  const hasCaptions = enableCaptions && state.captionTracks.length > 0;

  const collapsibleIds = useMemo(() => {
    const ids: string[] = [];
    ids.push('skip-back');                              // priority 1 (highest)
    ids.push('skip-forward');
    if (hasCaptions) ids.push('captions');
    return ids;
  }, [isVideo, hasCaptions]);

  // Hook computes mandatory width internally using breakpoint-aware metrics
  // that match the CSS container query rules in _react-player.scss
  const [controlsBottomRef, overflowed] = useResponsiveControls(collapsibleIds, isVideo);

  // Build overflow items for the ActionsMenu dropdown
  const overflowItems = useMemo<OverflowMenuItem[]>(() => {
    const items: OverflowMenuItem[] = [];

    if (overflowed.has('skip-back')) {
      items.push({
        id: 'skip-back',
        label: 'Back 5 seconds',
        type: 'action',
        onClick: handleSkipBack,
      });
    }

    if (overflowed.has('skip-forward')) {
      items.push({
        id: 'skip-forward',
        label: 'Forward 5 seconds',
        type: 'action',
        onClick: handleSkipForward,
      });
    }

    if (isVideo && waveformUrl) {
      items.push({
        id: 'waveform',
        label: 'Waveform',
        type: 'toggle' as const,
        isActive: state.waveformVisible,
        onClick: () => dispatch({ type: 'TOGGLE_WAVEFORM' }),
      });
    }

    if (overflowed.has('captions')) {
      items.push({
        id: 'captions',
        label: state.selectedTrackIdx >= 0
          ? `Captions: ${state.captionTracks[state.selectedTrackIdx]?.label ?? 'On'}`
          : 'Captions: Off',
        type: 'toggle',
        isActive: state.selectedTrackIdx >= 0,
        onClick: handleCaptionsOverflowToggle,
      });
    }

    return items;
  }, [
    overflowed,
    handleSkipBack,
    handleSkipForward,
    isVideo,
    waveformUrl,
    state.waveformVisible,
    state.selectedTrackIdx,
    state.captionTracks,
    handleCaptionsOverflowToggle,
  ]);

  return (
    <div
      ref={containerRef}
      className={`kollab-player ${isVideo ? 'kollab-player--video' : 'kollab-player--audio'}`}
      role="application"
      aria-label={isVideo ? 'Video Player' : 'Audio Player'}
      tabIndex={0}
      onMouseMove={resetHideTimer}
    >
      {/* Error overlays — portalled into wrapper so they cover the full
          player area (video + controls), not just the React mount point.
          Three variants:
            1. Unplayable source w/ proxy available → simple message + switch button
            2. Encoding in progress (no proxy yet) → polling progress overlay
            3. Generic error / download fallback → download overlay */}
      {fullscreenTargetRef.current && createPortal(
        errorState.hasError && state.activeSourceIndex > 0 && sources.length > 1 ? (
          <UnplayableSourceOverlay
            visible={errorState.hasError}
            message={errorState.message || 'This file cannot be played by your browser but a proxy is available.'}
            onSwitchToProxy={() => handleSourceChange(0)}
          />
        ) : errorState.isEncoding ? (
          <EncodingProgressOverlay
            visible={errorState.hasError}
            fileId={fileId}
            downloadUrl={downloadUrl}
            title={fileTitle}
            fileTypeName={fileTypeName}
            fileSize={fileSize}
            fileIconUrl={fileIconUrl}
          />
        ) : (
          <DownloadOverlay
            visible={errorState.hasError}
            message={errorState.message}
            canDownload={canDownload}
            downloadUrl={downloadUrl}
            title={fileTitle}
            fileTypeName={fileTypeName}
            fileSize={fileSize}
            fileIconUrl={fileIconUrl}
          />
        ),
        fullscreenTargetRef.current,
      )}

      {/* Loading overlay — portalled into wrapper so it covers the full
          player area (video + controls), not just the React mount point. */}
      {isVideo && state.isBuffering && !errorState.hasError && fullscreenTargetRef.current &&
        createPortal(
          <div className="kollab-player-loading" aria-label="Loading">
            <div className="spinner" />
          </div>,
          fullscreenTargetRef.current,
        )
      }

      {/* Caption overlay — absolute within parent for video, inline for audio */}
      {!errorState.hasError && !annotationEditorActive && <CaptionOverlay text={state.activeCueText} />}

      {/* Heatmap overlay — positioned at bottom of video (just above controls) */}
      {!errorState.hasError && (
        <HeatmapOverlay
          visible={seekBarHovered && enableHeatmap && state.showHeatmapsOnHover}
          fileId={fileId}
          left={heatmapRect?.left}
          width={heatmapRect?.width}
        />
      )}

      {/* Video overlay layer — mattes and safe area guides */}
      {isVideo && !errorState.hasError && fullscreenTargetRef.current && mediaRef.current &&
        (mediaRef.current as HTMLVideoElement).videoWidth > 0 &&
        createPortal(
          <VideoOverlayLayer
            videoElement={mediaRef.current as HTMLVideoElement}
            activeMatte={activeMatte}
            activeSafeAreas={activeSafeAreas}
            hidden={annotationEditorActive}
          />,
          fullscreenTargetRef.current,
        )
      }

      {/* Status overlay — portalled into wrapper so it positions in the video area */}
      {isVideo && !errorState.hasError && fullscreenTargetRef.current && createPortal(
        <StatusOverlay
          message={overlayStatus.message}
          visible={overlayStatus.visible}
        />,
        fullscreenTargetRef.current,
      )}

      {/* Side panel — portalled into wrapper in fullscreen video mode.
          Always rendered so the content ref is available for element adoption;
          the toggle button is hidden when no element has been set. */}
      {isVideo && state.isFullscreen && !errorState.hasError && fullscreenTargetRef.current && createPortal(
        <SidePanel
          isOpen={state.sidePanelOpen}
          onToggle={() => dispatch({ type: 'TOGGLE_SIDE_PANEL' })}
          controlsVisible={state.controlsVisible}
          contentRef={sidePanelContentRef}
          hasElement={state.sidePanelHasElement}
          tooltip={sidePanelTooltip}
        />,
        fullscreenTargetRef.current,
      )}

      {/* Waveform display — portalled into wrapper */}
      {showWaveform && fullscreenTargetRef.current &&
        createPortal(
          <WaveformDisplay
            waveformUrl={waveformUrl}
            waveformColor={waveformColor}
            currentTime={state.currentTime}
            duration={state.duration}
            isPlaying={state.isPlaying}
            isSeeking={state.isSeeking}
            commentMarkers={state.showMarkers ? comments : undefined}
            fps={fps}
            startFrame={startFrame}
            dropFrame={dropFrame}
            onSeekStart={handleSeekStart}
            onSeekEnd={handleSeekEnd}
          />,
          fullscreenTargetRef.current,
        )
      }

      {/* Controls — in document flow below the media element */}
      {!errorState.hasError && <ControlBar
        visible={state.controlsVisible}
        isVideo={isVideo}
        onMouseEnter={handleControlsMouseEnter}
        onMouseLeave={handleControlsMouseLeave}
      >
        <div className="kollab-player-controls-top">
          {fps ? (
            <TimecodePopover
              value={state.currentTime}
              duration={state.duration}
              fps={fps}
              startFrame={startFrame}
              dropFrame={dropFrame}
              isOpen={state.timecodePopoverOpen}
              onToggle={handleToggleTimecodePopover}
              onSeek={handleSeekEnd}
            />
          ) : (
            <div className="kollab-player-timecode-container">
              <TimecodeDisplay value={state.currentTime} />
            </div>
          )}
          <SeekBar
            currentTime={state.currentTime}
            duration={state.duration}
            bufferedEnd={state.bufferedEnd}
            mediaElementId={effectiveMediaElementId}
            onSeekStart={handleSeekStart}
            onSeekEnd={handleSeekEnd}
            fps={fps}
            startFrame={startFrame}
            dropFrame={dropFrame}
            thumbnailSpriteUrl={thumbnailSpriteUrl}
            comments={state.showMarkers ? comments : undefined}
            onMarkerClick={handleMarkerSeek}
            barRefCallback={seekBarRefCallback}
            onHoverChange={handleSeekBarHover}
          />
          <div className="kollab-player-timecode-container kollab-player-duration-container">
            <TimecodeDisplay
              value={durationMode === 'remaining'
                ? Math.max(0, state.duration - state.currentTime)
                : state.duration}
              fps={fps}
              startFrame={0}
              dropFrame={false}
              prefix={durationMode === 'remaining' ? '−' : undefined}
              onClick={toggleDurationMode}
            />
          </div>
        </div>

        <div ref={controlsBottomRef} className="kollab-player-controls-bottom">
          <div className="kollab-player-controls-left">
            {!isFollower && (
              <PlayButton
                isPlaying={state.isPlaying}
                mediaElementId={effectiveMediaElementId}
                onClick={handlePlayClick}
              />
            )}
            {!isFollower && !overflowed.has('skip-back') && (
              <SkipBackButton
                mediaElementId={effectiveMediaElementId}
                onClick={handleSkipBack}
              />
            )}
            {!isFollower && !overflowed.has('skip-forward') && (
                <SkipForwardButton
                    mediaElementId={effectiveMediaElementId}
                    onClick={handleSkipForward}
                />
            )}
            {isLeader && syncedSessionManager && (
              <FollowerReadyButton manager={syncedSessionManager} />
            )}
            <VolumeControl
              volume={state.volume}
              isMuted={state.isMuted}
              mediaElementId={effectiveMediaElementId}
              onVolumeChange={handleVolumeChange}
              onMuteToggle={handleMuteToggle}
            />
          </div>

          <div className="kollab-player-controls-right">
            {enableCaptions && !overflowed.has('captions') && (
              <CaptionsButton
                tracks={state.captionTracks}
                selectedTrackIdx={state.selectedTrackIdx}
                isOpen={state.captionPickerOpen}
                onToggle={handleToggleCaptionPicker}
                onSelectTrack={handleCaptionSelect}
                speechEnabled={speechEnabled}
                speechSupported={speechSupported}
                onToggleSpeech={handleToggleSpeech}
                onOpenSpeechSettings={handleOpenSpeechSettings}
              />
            )}
            <ActionsMenu
              isOpen={state.actionsMenuOpen}
              playbackRate={state.playbackRate}
              isLooping={state.isLooping}
              sources={sources}
              activeSourceIndex={state.activeSourceIndex}
              overflowItems={overflowItems}
              onToggle={handleToggleActionsMenu}
              onSpeedChange={handleSpeedChange}
              onSourceChange={handleSourceChange}
              onToggleLoop={handleToggleLoop}
              onOpenSettings={handleOpenSettings}
              overlayMatte={isVideo ? activeMatte : undefined}
              overlaySafeAreas={isVideo ? activeSafeAreas : undefined}
              onOverlayMatteChange={isVideo ? setActiveMatte : undefined}
              onToggleOverlaySafeArea={isVideo ? toggleSafeArea : undefined}
              isFollower={isFollower}
            />
            {isVideo && (
              <FullscreenButton
                isFullscreen={state.isFullscreen}
                onClick={toggleFullscreen}
              />
            )}
          </div>
        </div>
      </ControlBar>}

    </div>
  );
});

export default KollabPlayer;
